'use client';

import { useState, useMemo, useCallback, useRef } from 'react';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import {
  getDefaultVenueHighlights,
  getRestaurantHighlights,
  getSEVHighlights,
  getDMCOrCVBHighlights,
  getConventionCenterHighlights,
  type HighlightsProps,
  type VenueHighlightStrings
} from '@/utils/venueHighlightsHelper';
import Icon from '@/components/shared/Icons';
import type { Locale } from '@/i18n';
import useScrollEvent from '@/hooks/useScrollEvent';
import dynamic from 'next/dynamic';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

interface VenueHighlightProps {
  venue: CsnSearchVenue;
  measurementUnit: string;
  locale: Locale;
  highlightStrings: VenueHighlightStrings;
  isMapFlyout?: boolean;
  showSeperator?: boolean;
}

export interface Highlights {
  highlights: HighlightsProps[] | undefined;
}

function getHighlightsListAsPerVenueType(
  locale: Locale,
  venue: CsnSearchVenue,
  measurementUnit: string,
  localeStrings: VenueHighlightStrings
): HighlightsProps[] {
  switch (venue.type) {
    case 'RESTAURANT':
      return getRestaurantHighlights(locale, venue, measurementUnit, localeStrings);
    case 'DMC':
    case 'CVB':
      return getDMCOrCVBHighlights(locale, venue, measurementUnit, localeStrings);
    case 'CONVENTION_CENTER':
      return getConventionCenterHighlights(locale, venue, measurementUnit, localeStrings);
    case 'SPECIAL_EVENT_VENUE':
      return getSEVHighlights(locale, venue, measurementUnit, localeStrings);
    default:
      return getDefaultVenueHighlights(locale, venue, measurementUnit, localeStrings);
  }
}

export default function VenueHighlight({
  venue,
  measurementUnit,
  locale,
  highlightStrings,
  isMapFlyout,
  showSeperator
}: VenueHighlightProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dismissVenueStats = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onMouseEntershowVenueStats = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();
      if (window !== undefined && e.type === 'mouseenter') {
        window.addEventListener('scroll', dismissVenueStats, false);
      }
      setIsOpen(!isOpen);
    },
    [dismissVenueStats, isOpen]
  );

  const onClickshowVenueStats = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();
      if (window !== undefined) {
        window.addEventListener('scroll', dismissVenueStats, false);
      }
      setIsOpen(!isOpen);
    },
    [dismissVenueStats, isOpen]
  );

  const onKeyDownshowVenueStats = useCallback(
    async e => {
      if (e.key === 'Enter') {
        setIsOpen(!isOpen);
      }
      if (e.key === ' ') {
        e.stopPropagation();
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [setIsOpen, isOpen]
  );

  useScrollEvent(isOpen, dismissVenueStats);

  const highlights = useMemo(
    () => getHighlightsListAsPerVenueType(locale, venue, measurementUnit, highlightStrings),
    [locale, highlightStrings, measurementUnit, venue]
  );
  const filteredhighlights = useMemo(
    () =>
      highlights?.filter(
        highlight =>
          highlight.highlightName === highlightStrings._meeting_rooms ||
          highlight.highlightName === highlightStrings._largest_meeting_room ||
          highlight.highlightName === highlightStrings._guest_rooms
      ),
    [highlights, highlightStrings._guest_rooms, highlightStrings._largest_meeting_room, highlightStrings._meeting_rooms]
  );
  return (
    <div className={`${isMapFlyout ? '-ml-0.5' : 'flex truncate pb-2 pt-1.5 sm:pt-2.5'}`}>
      {showSeperator && filteredhighlights.length > 0 && (
        <div className="mx-2 hidden text-b-md sm:mr-3 sm:block">|</div>
      )}
      <div className="truncate">
        <div
          role="button"
          tabIndex={0}
          className={`group flex items-center gap-1 truncate text-b-xs sm:grid-cols-2 sm:pt-0.5 ${
            venue.listingCode ? 'text-hb-neutral-60' : 'text-neutral-60'
          }`}
          onMouseEnter={onMouseEntershowVenueStats}
          onMouseLeave={dismissVenueStats}
          onClick={onClickshowVenueStats}
          onKeyDown={onKeyDownshowVenueStats}
          aria-label="venue highlights"
          ref={triggerRef}
        >
          {filteredhighlights?.map((highlight, index) => (
            <div
              className={`flex text-b-xs ${index + 1 === filteredhighlights.length ? 'relative truncate' : ''}`}
              aria-label="venue highlights"
              /* eslint-disable-next-line react/no-array-index-key, react/jsx-indent */
              key={`${highlight.highlightValue}-${index}`}
            >
              <div className="relative flex">
                <Icon
                  id={highlight.highlightIcon}
                  className="h-4 w-4 fill-neutral-60 group-hover:fill-interactive-70"
                />
              </div>
              <span className={`ml-1 ${index + 1 === filteredhighlights.length ? 'truncate' : ''}`}>
                <span className="w-full overflow-hidden truncate text-b-xs text-neutral-90 group-hover:text-interactive-70">
                  {highlight.highlightValue}
                </span>
              </span>
              {index < filteredhighlights.length - 1 && (
                <span className="mx-1 mb-2.5 ml-1.5 h-1 w-1 group-hover:text-interactive-70">•</span>
              )}
            </div>
          ))}
        </div>
        {isOpen && (
          <Popper
            triggerRef={triggerRef}
            isOpen={isOpen}
            placement="bottom-start"
            className="min-h-32 relative rounded-xl bg-background"
          >
            <div className="min-h-36 min-w-40 flex flex-col justify-start gap-2 rounded-xl bg-background p-4 shadow-4">
              {highlights?.map((highlight, index) => (
                /* eslint-disable-next-line react/no-array-index-key */
                <div className="flex justify-start p-1 sm:p-0.5" key={`${highlight.highlightValue}-${index}`}>
                  <Icon id={highlight.highlightIcon} className="h-4 w-4 fill-neutral-60" />
                  <span className="ml-1 text-b-xs text-neutral-60">
                    {highlight.highlightName}: <span className="text-neutral-90">{highlight.highlightValue}</span>
                  </span>
                </div>
              ))}
            </div>
          </Popper>
        )}
      </div>
    </div>
  );
}
